import React, { useRef, useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import BannerSlot from '../../banners/BannerOverrides';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { generateProductUrl } from '../../../utils/productUrl';
import { getProductPrice } from '../../../utils/productPrice';
import AddToCartModal from '../../carrito/AddToCartModal';
import FavoriteButton from '../../shared/FavoriteButton';
import { staggerContainerVariants, staggerItemLeftVariants } from '../../animations/ScrollAnimations';
import { useConfiguracionGeneral } from '../../../hooks/useConfiguracionGeneral';
import { cmsColorStyle } from '../../../utils/cmsColor';

const SectionWrapper = styled.section`
  background-color: var(--color-marron-tercero);
  border-radius: var(--radius-xl);
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 0px;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 30px 20px;
    padding-right: 0 !important;
    padding-left: 20px;
    gap: 30px;
  }
`;

const HaloLuz = styled.img`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 1000px;
  z-index: 0;
  pointer-events: none;
  /* Reservar espacio antes de cargar para evitar CLS */
  aspect-ratio: 1000 / 800;
  height: auto;
`;

const TopHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 20px;
  }
`;

const TextBlock = styled.div`
  max-width: 50%;
  padding-left: 60px;
  
  @media (max-width: 768px) {
    max-width: 100%;
    display: contents;
  }
`;

const Title = styled.h2`
  font-family: var(--font-family-primary);
  font-size: clamp(3.5rem, 5vw, 6rem);
  line-height: 0.95;
  font-weight: 600;
  margin-bottom: 15px;
  letter-spacing: -0.02em;

  .italic-text {
    color: var(--color-blanco);
    font-style: italic;
    display: block;
  }

  .gold-text {
    color: var(--color-titulo-marybe);
    font-weight: 400;
    display: block;
  }
  
  @media (max-width: 768px) {
    font-size: 3.3rem;
    order: 1;
  }

    @media (max-width: 400px) {
    font-size: 2.7rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.35rem;
  color: var(--color-fondo-beneficio-tarjeta);
  max-width: 350px;
  line-height: 1.35;
  letter-spacing: -1%;
  
  @media (max-width: 768px) {
    margin: 0 auto;
    order: 3;
    max-width: 80vw;
  }
`;

const FeaturedPicture = styled.picture`
  width: 50%;
  max-height: 40vh;
  max-width: 100%;
  padding-right: 60px;
  display: flex;
  justify-content: center;
  
  @media (max-width: 768px) {
    width: 100%;
    order: 2;
    padding: 0 5px;
  }

  img {
    width: 100%;
    height: 100%;
    max-height: 45vh;
    object-fit: contain;
    filter: drop-shadow(0 20px 30px rgba(0,0,0,0.5));
  }
`;

const ProductsGrid = styled(motion.div)`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  gap: 30px;
  position: relative;
  z-index: 1;
  cursor: grab;
  margin-left: 60px;
  padding-right: 60px; /* Para mantener simetría al final del scroll */
  padding-bottom: 10px;
  min-height: 470px; /* Reservar espacio vertical exacto para evitar CLS en la carga */

  &:active {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 1250px) and (max-width: 1524px) {
    margin-top: -5vh;
  }

  @media (max-width: 1024px) {
    gap: 30px;
    margin-left: 60px;
    padding-right: 40px;
  }

  @media (max-width: 770px) {
    margin-left: 0px;
    padding-right: 0px;
  }

  @media (max-width: 600px) {
    gap: 20px;
    margin-left: 0px; /* El padre ya tiene padding lateral en mobile */
    padding-right: 0px;
    min-height: 430px;
  }
`;

const SkeletonProductCard = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s ease-in-out infinite;
  border-radius: 20px;
  padding: 16px;
  flex-shrink: 0;
  height: 460px;

  @keyframes shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  /* Misma anchura matemática que ProductCard */
  width: 290px;
  @media (max-width: 1600px) { width: 290px; }
  @media (max-width: 1400px) { width: 290px; }
  @media (max-width: 997px) { width: 290px; }
  @media (max-width: 600px) { width: 290px; height: 420px; }
`;

const ProductCard = styled(motion.div)`
  background-color: var(--color-blanco);
  border-radius: 20px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: transform 0.3s ease;
  flex-shrink: 0;
  scroll-snap-align: start;
  user-select: none;
  -webkit-user-drag: none;
  
  
  /* Pantallas muy grandes (> 1600px): 5.5 tarjetas */
  width: 290px;
  
  @media (max-width: 1600px) {
    /* Pantallas grandes (1401px - 1600px): 4.5 tarjetas */
    width: 290px;
  }

  @media (max-width: 1400px) {
    /* Notebooks (998px - 1400px): 3.5 tarjetas */
    width: 290px;
  }

  @media (max-width: 997px) {
    /* Tablets (601px - 997px): 2.5 tarjetas */
    width: 280px;
  }

  @media (max-width: 600px) {
    /* Mobile (<= 600px): 1.5 tarjetas */
    width: 240px;
    padding: 12px;
  }
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const CardImageContainer = styled.div`
  width: 100%;
  height: 200px;
  background-color: #fff;
  border-radius: var(--radius-md);
  margin-bottom: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;
  cursor: pointer;

  @media (max-width: 600px) {
    height: 140px;
    margin-bottom: 10px;
  }
  
  > svg {
    width: 60px;
    height: 60px;
    opacity: 0.1;
  }
`;

const HeartContainer = styled.div`
  position: absolute;
  bottom: 15px;
  right: 15px;
  z-index: 2;
`;



const ProductBrand = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-marron-secundario);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  letter-spacing: 10%;
  margin-bottom: 4px;

  @media (max-width: 600px) {
    margin-bottom: 4px;
  }
`;

const ProductName = styled.h3`
  font-size: 16px;
  color: black;
  font-family: var(--font-family-secondary);
  font-weight: 400;
  margin-bottom: 15px;
  line-height: 1.2;
  letter-spacing: 0%;
  cursor: pointer;

  @media (max-width: 600px) {
    font-size: 14px;
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 10px;
  row-gap: 4px;
  margin-bottom: 6px;

  @media (max-width: 1024px) {
    column-gap: 6px;
  }
`;

const OldPrice = styled.span`
  font-size: 0.85rem;
  color: #a0a0a0;
  text-decoration: line-through;
`;

const CurrentPrice = styled.span`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-bordo-secundario);

  @media (max-width: 600px) {
    font-size: 1.1rem;
  }
`;

const DiscountBadge = styled.span`
  background-color: var(--color-bordo-secundario);
  color: white;
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: bold;
`;

const Installments = styled.div`
  font-size: 0.85rem;
  color: #535353;
  margin-bottom: 6px;
  font-weight: 600;

  @media (max-width: 600px) {
    font-size: 0.75rem;
  }
`;

const LegalText = styled.div`
  font-size: 0.7rem;
  color: #b0b0b0;
  margin-bottom: 20px;
  font-weight: 400;

  @media (max-width: 600px) {
    margin-bottom: 12px;
    font-size: 0.65rem;
  }
`;

const AddButton = styled.button`
  background-color: var(--color-marron-principal);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 16px 24px;
  font-weight: 500;
  font-size: 1rem;
  letter-spacing: 2%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  transition: background-color 0.2s;
  margin-top: auto;

  @media (max-width: 600px) {
    padding: 12px 16px;
    font-size: 0.9rem;
    border-radius: 10px;
  }

  &:hover {
    background-color: var(--color-bordo-secundario);
  }
  
  svg {
    width: 20px;
    height: 20px;
    fill: none;
    stroke: currentColor;

    @media (max-width: 600px) {
      width: 18px;
      height: 18px;
    }
  }
`;

const ScrollWrapper = styled.div`
  position: relative;
`;

const ArrowBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $side }) => $side}: 10px;
  z-index: 10;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: none;
  background-color: rgba(255, 255, 255, 0.92);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);
  color: var(--color-marron-principal);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background-color: #fff;
    transform: translateY(-50%) scale(1.08);
  }

  svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    stroke-width: 2.5;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const BannersRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  position: relative;
  z-index: 1;
  padding: 10px 200px 30px 200px;
  gap: 30px;
  
  @media (max-width: 1200px) {
  padding: 10px 80px 30px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 10px 20px 30px 20px;
  }
`;

const BottomBanner = styled.div`
  background-color: var(--color-marron-cuarto);
  padding: 20px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  overflow: hidden;
  min-height: 280px;
  justify-content: flex-start;
  box-shadow: inset 0 0 50px rgba(0,0,0,0.2);
`;

const BannerTitle = styled.h3`
  font-family: var(--font-family-primary);
  font-size: 35px;
  color: var(--color-blanco);
  letter-spacing: -0.02em;
  font-style: italic;
  font-weight: 600;
  margin-bottom: auto;
  z-index: 2;
  
  @media (max-width: 1024px) {
    font-size: 1.5rem;
  }
`;

const BannerButton = styled.button`
  background-color: var(--color-blanco);
  color: var(--color-marron-cuarto);
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: 500;
  font-size: 0.9rem;
  z-index: 2;
  transition: transform 0.2s, background-color 0.2s;
  margin-top: 15px;
  
  &:hover {
    transform: translateY(-2px);
    background-color: var(--color-blanco-pero-no-tan-blanco);
  }
`;

const BannerImageWrapper = styled.div`
  width: 100%;
  height: 140px;
  position: absolute;
  bottom: -5px;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 1;
  
  img {
    height: 220px;
    object-fit: contain;
    filter: drop-shadow(0 10px 15px rgba(0,0,0,0.4));
  }
`;

// Helper SVG Icons


const CartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 22C8.55228 22 9 21.5523 9 21C9 20.4477 8.55228 20 8 20C7.44772 20 7 20.4477 7 21C7 21.5523 7.44772 22 8 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 22C19.5523 22 20 21.5523 20 21C20 20.4477 19.5523 20 19 20C18.4477 20 18 20.4477 18 21C18 21.5523 18.4477 22 19 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2.0498 2.05005H4.0498L6.7098 14.47C6.80738 14.9249 7.06048 15.3315 7.42552 15.6199C7.79056 15.9083 8.24471 16.0604 8.7098 16.05H18.4898C18.945 16.0493 19.3863 15.8933 19.7408 15.6079C20.0954 15.3224 20.3419 14.9246 20.4398 14.48L22.0898 7.05005H5.1198" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ImagePlaceholder = () => (
  <svg viewBox="0 0 24 24" fill="#ccc">
    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
  </svg>
);

const ChevronLeft = () => (
  <svg viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6" /></svg>
);
const ChevronRight = () => (
  <svg viewBox="0 0 24 24"><path d="M9 18l6-6-6-6" /></svg>
);

export default function FeaturedSection({ seccion = 'perfumeria' }) {
  const { config } = useConfiguracionGeneral();
  const [productos, setProductos] = useState([]);
  const [bannersDestacados, setBannersDestacados] = useState([]);
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const isDragging = useRef(false);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [seccionInfo, setSeccionInfo] = useState(null);

  const handleAddClick = (product, e) => {
    e.stopPropagation();
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const seccionName = seccion === 'hogar' ? 'Hogar' : 'Perfumería';

  useEffect(() => {
    fetch(`${process.env.REACT_APP_STRAPI_URL}/api/productos?filters[publicado][$eq]=true&filters[destacado][$eq]=true&filters[seccion][$eq]=${seccionName}&populate=*`)
      .then(res => res.json())
      .then(data => {
        if (data && data.data) {
          setProductos(data.data);
        }
      })
      .catch(err => console.error('Error fetching productos:', err));

    // Obtener la información de la Sección Principal y los banners de Categorías Destacadas
    fetch(`${process.env.REACT_APP_STRAPI_URL}/api/seccion-principal?populate[secciones][populate]=*&populate[categorias_destacadas][populate]=*`)
      .then(res => res.json())
      .then(data => {
        if (data && data.data) {
          const attrs = data.data.attributes || data.data;
          const secciones = attrs.secciones || [];
          const info = secciones.find(s => s.seccion?.toLowerCase() === 'perfumeria' || s.seccion?.toLowerCase() === 'perfumería');
          if (info) {
            setSeccionInfo(info);
          }
          if (seccion !== 'hogar' && attrs.categorias_destacadas) {
            setBannersDestacados(attrs.categorias_destacadas);
          }
        }
      })
      .catch(err => console.error('Error fetching seccion-principal:', err));

  }, [seccionName, seccion]);

  const isDown = useRef(false);
  const startX = useRef(0);
  const scrollLeftVal = useRef(0);

  const handleInteract = useCallback(() => { }, []);

  const handleMouseDown = useCallback((e) => {
    const el = scrollRef.current;
    if (!el) return;
    isDown.current = true;
    isDragging.current = false;
    el.style.scrollSnapType = 'none';
    el.style.scrollBehavior = 'auto';
    startX.current = e.pageX - el.offsetLeft;
    scrollLeftVal.current = el.scrollLeft;
    handleInteract();
  }, [handleInteract]);

  const handleMouseLeave = useCallback(() => {
    if (!isDown.current) return;
    isDown.current = false;
    const el = scrollRef.current;
    if (el) {
      el.style.scrollSnapType = 'x mandatory';
      el.style.scrollBehavior = '';
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!isDown.current) return;
    isDown.current = false;
    const el = scrollRef.current;
    if (el) {
      el.style.scrollSnapType = 'x mandatory';
      el.style.scrollBehavior = '';
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDown.current) return;
    e.preventDefault();
    const el = scrollRef.current;
    if (!el) return;
    const x = e.pageX - el.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    if (Math.abs(walk) > 5) {
      isDragging.current = true;
    }
    el.scrollLeft = scrollLeftVal.current - walk;
    handleInteract();
  }, [handleInteract]);

  const scrollByStep = useCallback((dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const first = el.children[0];
    const gap = 30;
    const step = first ? first.getBoundingClientRect().width + gap : el.clientWidth;
    el.scrollTo({ left: el.scrollLeft + dir * step, behavior: 'smooth' });
  }, []);

  const handleBannerClick = (link) => {
    let target = link || '/tienda';
    if (target.startsWith('http://') || target.startsWith('https://')) {
      try {
        const url = new URL(target);
        if (url.origin === window.location.origin) {
          target = url.pathname + url.search + url.hash;
          navigate(target);
          window.scrollTo({ top: 0, behavior: 'instant' });
        } else {
          window.location.href = target;
        }
      } catch (e) {
        window.location.href = target;
      }
    } else {
      if (!target.startsWith('/')) {
        target = '/' + target;
      }
      navigate(target);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  };

  const handleProductClick = (id, nombre) => {
    if (!isDragging.current) {
      navigate(generateProductUrl(id, nombre));
    }
  };

  const formatPrice = (price) => {
    if (!price) return '$0';
    return '$' + Number(price).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  return (
    <SectionWrapper>
      <HaloLuz src="/inicio/halo-luz.webp" alt="Efecto de luz" width="1000" height="800" fetchPriority="high" loading="eager" decoding="sync" />
      <TopHeader>
        <TextBlock>
          <Title>
            <span className="italic-text" style={cmsColorStyle(seccionInfo?.color_titulo_cursiva)}>
              {seccionInfo?.titulo_cursiva || 'Lo nuevo'}
            </span>
            <span className="gold-text" style={cmsColorStyle(seccionInfo?.color_titulo_normal)}>
              {seccionInfo?.titulo_normal || 'en Marybe'}
            </span>
          </Title>
          <Subtitle>
            {seccionInfo?.subtitulo || 'Intensidad, seducción y carácter en un solo lugar.'}
          </Subtitle>
        </TextBlock>
        <BannerSlot name="featured_perfumeria" side><FeaturedPicture>
          {seccion === 'hogar' ? (
            <img src="/inicio/hogar-featured.webp" alt="Hogar destacado" style={{ maxHeight: '42vh' }} loading="eager" decoding="sync" />
          ) : (
            <>
              {seccionInfo?.imagen_mobile?.url ? (
                <source media="(max-width: 768px)" srcSet={`${seccionInfo.imagen_mobile.url.startsWith('http') ? '' : process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337'}${seccionInfo.imagen_mobile.url}`} />
              ) : (
                <source media="(max-width: 768px)" srcSet="/inicio/fragancias-mobile.webp" />
              )}
              
              <img
                src={
                  seccionInfo?.imagen_desktop?.url
                    ? (seccionInfo.imagen_desktop.url.startsWith('http') ? seccionInfo.imagen_desktop.url : `${process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337'}${seccionInfo.imagen_desktop.url}`)
                    : '/inicio/featured.webp'
                }
                alt="Fragancias destacadas"
                loading="eager"
                decoding="sync"
              />
            </>
          )}
        </FeaturedPicture></BannerSlot>
      </TopHeader>

      <ScrollWrapper>
        <ArrowBtn $side="left" type="button" aria-label="Anterior" onClick={() => scrollByStep(-1)}>
          <ChevronLeft />
        </ArrowBtn>
        <ProductsGrid
          ref={scrollRef}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          onScroll={handleInteract}
          onTouchStart={handleInteract}
          variants={staggerContainerVariants}
          initial="hidden"
          animate="show"
        >
          {productos.length === 0 ? (
            <>
              <SkeletonProductCard />
              <SkeletonProductCard />
              <SkeletonProductCard />
              <SkeletonProductCard />
              <SkeletonProductCard />
            </>
          ) : (
            productos.map((item, index) => {
              const id = item.id || item.documentId || `prod-${index}`;
              const attrs = item.attributes || item;

              const nombre = attrs.nombre;
              const marca = attrs.marca;
              const { price, offerPrice, calcDescuento: descuentoCalc } = getProductPrice(attrs);

              let imgUrl = null;
              const getFullUrl = (url) => url?.startsWith('http') ? url : `${process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337'}${url}`;
              if (attrs.portada?.data?.attributes?.url) {
                imgUrl = getFullUrl(attrs.portada.data.attributes.url);
              } else if (attrs.portada?.url) {
                imgUrl = getFullUrl(attrs.portada.url);
              }

              return (
                <ProductCard key={id} variants={staggerItemLeftVariants}>
                  <CardImageContainer onClick={() => handleProductClick(id, nombre)}>
                    {imgUrl ? (
                      <img src={imgUrl} alt={nombre} style={{ width: '100%', height: '100%', objectFit: 'contain' }} draggable="false" loading="eager" decoding="sync" />
                    ) : (
                      <ImagePlaceholder />
                    )}
                    <HeartContainer>
                      <FavoriteButton product={item} />
                    </HeartContainer>
                  </CardImageContainer>

                  <ProductBrand>{marca}</ProductBrand>
                  <ProductName onClick={() => handleProductClick(id, nombre)}>{nombre}</ProductName>

                  <PriceRow>
                    {offerPrice && <OldPrice>{formatPrice(price)}</OldPrice>}
                    <CurrentPrice>{formatPrice(offerPrice || price)}</CurrentPrice>
                    {descuentoCalc > 0 && <DiscountBadge>{descuentoCalc}% OFF</DiscountBadge>}
                  </PriceRow>

                    {config?.cuotas_activas && (
                      <Installments>
                        {config?.cuotas_texto_previo ? config.cuotas_texto_previo + ' ' : '3 cuotas sin interés de '}{formatPrice(Math.round((offerPrice || price) / (config?.cuotas_cantidad || 3)))}
                      </Installments>
                    )}
                  <LegalText>
                    Precio sin impuestos nacionales {formatPrice(Math.round((offerPrice || price) * 0.79))}
                  </LegalText>

                  <AddButton onClick={(e) => handleAddClick(item, e)}>
                    Agregar <CartIcon />
                  </AddButton>
                </ProductCard>
              );
            }))}
        </ProductsGrid>
        <ArrowBtn $side="right" type="button" aria-label="Siguiente" onClick={() => scrollByStep(1)}>
          <ChevronRight />
        </ArrowBtn>
      </ScrollWrapper>

      {seccion !== 'hogar' && (
        <BannersRow>
          {bannersDestacados && bannersDestacados.length > 0 ? (
            bannersDestacados.map((banner, index) => {
              const getFullUrl = (url) => url?.startsWith('http') ? url : `${process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337'}${url}`;
              let imgUrl = '';
              if (banner.imagen?.data?.attributes?.url) {
                imgUrl = getFullUrl(banner.imagen.data.attributes.url);
              } else if (banner.imagen?.url) {
                imgUrl = getFullUrl(banner.imagen.url);
              }
              return (
                <BannerSlot key={index} name={`categoria_${index}`}><BottomBanner>
                  <BannerTitle>{banner.titulo}</BannerTitle>
                  {imgUrl && (
                    <BannerImageWrapper>
                      <img src={imgUrl} alt={banner.titulo} width="220" height="220" loading="eager" decoding="sync" />
                    </BannerImageWrapper>
                  )}
                  <BannerButton onClick={() => handleBannerClick(banner.link_boton)}>
                    {banner.texto_boton}
                  </BannerButton>
                </BottomBanner></BannerSlot>
              );
            })
          ) : (
            <>
              <BannerSlot name="categoria_0"><BottomBanner>
                <BannerTitle>El poder del elixir</BannerTitle>
                <BannerImageWrapper>
                  <img src="/inicio/elixir.webp" alt="El poder del elixir" width="220" height="220" loading="eager" decoding="sync" />
                </BannerImageWrapper>
                <BannerButton onClick={() => { navigate('/tienda?banner=elixir&seccion=Perfumer%C3%ADa'); window.scrollTo({ top: 0, behavior: 'instant' }); }}>Conocer más</BannerButton>
              </BottomBanner></BannerSlot>

              <BannerSlot name="categoria_1"><BottomBanner>
                <BannerTitle>Toda la línea de Azzaro</BannerTitle>
                <BannerImageWrapper>
                  <img src="/inicio/azzaro.webp" alt="Línea Azzaro" width="220" height="220" loading="eager" decoding="sync" />
                </BannerImageWrapper>
                <BannerButton onClick={() => { navigate('/tienda?banner=azzaro&seccion=Perfumer%C3%ADa'); window.scrollTo({ top: 0, behavior: 'instant' }); }}>Conocer más</BannerButton>
              </BottomBanner></BannerSlot>
            </>
          )}
        </BannersRow>
      )}

      {selectedProduct && (
        <AddToCartModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          product={selectedProduct}
          initialMode="select"
        />
      )}
    </SectionWrapper>
  );
}
